import type { FeedbackRating } from '../types/messages';

const SESSION_STORAGE_KEY = 'lizadocs_session_id';

/**
 * Get session ID from localStorage.
 */
function getSessionId(): string | null {
  try {
    return localStorage.getItem(SESSION_STORAGE_KEY);
  } catch {
    return null;
  }
}

/**
 * Submit or update feedback on a message.
 */
export async function submitFeedback(
  apiUrl: string,
  widgetId: string | null,
  messageId: string,
  rating: 'up' | 'down'
): Promise<void> {
  if (!widgetId) {
    console.warn('[LizaDocs] No widget ID, cannot submit feedback');
    return;
  }
  const sessionId = getSessionId();
  if (!sessionId) {
    console.warn('[LizaDocs] No session ID, cannot submit feedback');
    return;
  }

  const response = await fetch(buildFeedbackUrl(apiUrl, widgetId, '/feedback'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      message_id: messageId,
      session_id: sessionId,
      rating,
    }),
  });

  if (!response.ok) {
    throw new Error(`Failed to submit feedback: ${response.status}`);
  }
}

/**
 * Remove feedback from a message.
 */
export async function removeFeedback(
  apiUrl: string,
  widgetId: string | null,
  messageId: string
): Promise<void> {
  if (!widgetId) {
    console.warn('[LizaDocs] No widget ID, cannot remove feedback');
    return;
  }
  const sessionId = getSessionId();
  if (!sessionId) {
    console.warn('[LizaDocs] No session ID, cannot remove feedback');
    return;
  }

  const response = await fetch(
    buildFeedbackUrl(apiUrl, widgetId, `/feedback/${messageId}`, sessionId),
    { method: 'DELETE' }
  );

  if (!response.ok && response.status !== 404) {
    throw new Error(`Failed to remove feedback: ${response.status}`);
  }
}

/**
 * Get current feedback for a message.
 */
export async function getFeedback(
  apiUrl: string,
  widgetId: string | null,
  messageId: string
): Promise<FeedbackRating> {
  if (!widgetId) return null;
  const sessionId = getSessionId();
  if (!sessionId) return null;

  const response = await fetch(
    buildFeedbackUrl(apiUrl, widgetId, `/feedback/${messageId}`, sessionId)
  );

  if (!response.ok) return null;

  const data = await response.json();
  return data?.rating ?? null;
}

function buildFeedbackUrl(
  apiUrl: string,
  widgetId: string,
  path: string,
  sessionId?: string
): string {
  const url = new URL(`${apiUrl}${path}`);
  url.searchParams.set('widget_id', widgetId);
  if (sessionId) {
    url.searchParams.set('session_id', sessionId);
  }
  return url.toString();
}
